/**
 * Generic Form Sections
 * Reusable components for standard form sections
 * Used across all injury types
 */

import React from 'react';
import { OCCUPATION_CATEGORY_OPTIONS, HEAVIEST_LIFT_OPTIONS, WORK_STATUS_OPTIONS } from '../config/vocationalFields';

/**
 * Demographics Section
 * Collects patient demographic information
 */
export const DemographicsSection = ({ formData, onChange, customFields = [] }) => {
  const handleFieldChange = (fieldId, value) => {
    onChange(fieldId, value);
  };

  return (
    <div className="demographics-section">
      <style jsx>{`
        .demographics-section {
          display: grid;
          gap: 20px;
        }

        .field-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }

        .field-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .field-group.full-width {
          grid-column: 1 / -1;
        }

        .field-label {
          font-weight: 600;
          color: #1f2937;
          font-size: 14px;
        }

        .required {
          color: #dc2626;
          margin-left: 4px;
        }

        .form-input,
        .form-select {
          padding: 10px 12px;
          border: 2px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
          transition: border-color 0.2s;
        }

        .form-input:focus,
        .form-select:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }

        .radio-group {
          display: flex;
          gap: 20px;
        }

        .radio-option {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
        }

        .radio-option input {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        @media (max-width: 768px) {
          .field-row {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="field-row">
        <div className="field-group">
          <label className="field-label">
            First Name<span className="required">*</span>
          </label>
          <input
            type="text"
            value={formData.firstName || ''}
            onChange={(e) => handleFieldChange('firstName', e.target.value)}
            className="form-input"
            placeholder="John"
          />
        </div>

        <div className="field-group">
          <label className="field-label">
            Last Name<span className="required">*</span>
          </label>
          <input
            type="text"
            value={formData.lastName || ''}
            onChange={(e) => handleFieldChange('lastName', e.target.value)}
            className="form-input"
            placeholder="Smith"
          />
        </div>
      </div>

      <div className="field-row">
        <div className="field-group">
          <label className="field-label">
            Date of Birth<span className="required">*</span>
          </label>
          <input
            type="date"
            value={formData.dateOfBirth || ''}
            onChange={(e) => handleFieldChange('dateOfBirth', e.target.value)}
            className="form-input"
          />
        </div>

        <div className="field-group">
          <label className="field-label">
            Gender<span className="required">*</span>
          </label>
          <select
            value={formData.gender || ''}
            onChange={(e) => handleFieldChange('gender', e.target.value)}
            className="form-select"
          >
            <option value="">Select...</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
            <option value="Prefer not to say">Prefer not to say</option>
          </select>
        </div>
      </div>

      <div className="field-row">
        <div className="field-group">
          <label className="field-label">
            Ethnicity
          </label>
          <select
            value={formData.ethnicity || ''}
            onChange={(e) => handleFieldChange('ethnicity', e.target.value)}
            className="form-select"
          >
            <option value="">Select...</option>
            <option value="NZ European">NZ European</option>
            <option value="Māori">Māori</option>
            <option value="Samoan">Samoan</option>
            <option value="Cook Island Māori">Cook Island Māori</option>
            <option value="Tongan">Tongan</option>
            <option value="Niuean">Niuean</option>
            <option value="Chinese">Chinese</option>
            <option value="Indian">Indian</option>
            <option value="Filipino">Filipino</option>
            <option value="Korean">Korean</option>
            <option value="Japanese">Japanese</option>
            <option value="Sri Lankan">Sri Lankan</option>
            <option value="Middle Eastern">Middle Eastern</option>
            <option value="Latin American">Latin American</option>
            <option value="African">African</option>
            <option value="Other European">Other European</option>
            <option value="Other Asian">Other Asian</option>
            <option value="Other Pacific Peoples">Other Pacific Peoples</option>
            <option value="Other">Other</option>
            <option value="Prefer not to say">Prefer not to say</option>
          </select>
        </div>

        <div className="field-group">
          <label className="field-label">
            NHI Number
          </label>
          <input
            type="text"
            value={formData.nhi || ''}
            onChange={(e) => handleFieldChange('nhi', e.target.value)}
            className="form-input"
            placeholder="ABC1234"
            maxLength={7}
          />
        </div>
      </div>

      <div className="field-row">
        <div className="field-group">
          <label className="field-label">
            Phone Number<span className="required">*</span>
          </label>
          <input
            type="tel"
            value={formData.phone || ''}
            onChange={(e) => handleFieldChange('phone', e.target.value)}
            className="form-input"
            placeholder="021 123 4567"
          />
        </div>
      </div>

      <div className="field-group full-width">
        <label className="field-label">
          Email Address<span className="required">*</span>
        </label>
        <input
          type="email"
          value={formData.email || ''}
          onChange={(e) => handleFieldChange('email', e.target.value)}
          className="form-input"
          placeholder="john.smith@email.com"
        />
      </div>

      <div className="field-group full-width">
        <label className="field-label">
          Address
        </label>
        <input
          type="text"
          value={formData.address || ''}
          onChange={(e) => handleFieldChange('address', e.target.value)}
          className="form-input"
          placeholder="123 Main Street, Hamilton"
        />
      </div>

      <div className="field-row">
        <div className="field-group">
          <label className="field-label">
            Occupation
          </label>
          <input
            type="text"
            value={formData.occupation || ''}
            onChange={(e) => handleFieldChange('occupation', e.target.value)}
            className="form-input"
            placeholder="e.g., Teacher, Builder"
          />
        </div>

        <div className="field-group">
          <label className="field-label">
            Employer (if injury work-related)
          </label>
          <input
            type="text"
            value={formData.employer || ''}
            onChange={(e) => handleFieldChange('employer', e.target.value)}
            className="form-input"
            placeholder="Company name"
          />
        </div>
      </div>

      {/*
        Vocational classification (occupation category + physical demand).
        Definitions/value sets ported one-time from ara-health — see provenance
        in frontend/src/config/vocationalFields.js. Stored in the demographics
        JSON blob (no schema migration); the A1 ClinHx->Ara mapping is a direct
        copy of these value tokens.
      */}
      <div className="field-row">
        <div className="field-group">
          <label className="field-label">
            Which category best fits your occupation?
          </label>
          <select
            value={formData.occupationCategory || ''}
            onChange={(e) => handleFieldChange('occupationCategory', e.target.value)}
            className="form-input"
          >
            <option value="">Select…</option>
            {OCCUPATION_CATEGORY_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="field-group">
          <label className="field-label">
            What is the typical heaviest lift in your normal work?
          </label>
          <div className="radio-group">
            {HEAVIEST_LIFT_OPTIONS.map(opt => (
              <label className="radio-option" key={opt.value}>
                <input
                  type="radio"
                  name="heaviestLift"
                  value={opt.value}
                  checked={formData.heaviestLift === opt.value}
                  onChange={(e) => handleFieldChange('heaviestLift', e.target.value)}
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/*
        Patient-reported work status (dual work-status, patient side). Ported
        one-time from ara-health current_work_status — see vocationalFields.js.
        The referrer-reported side is extracted from the referral letter into
        Referral.referrerReportedWorkStatus so a discrepancy is preserved.
      */}
      <div className="field-row">
        <div className="field-group full-width">
          <label className="field-label">
            What is your work status right now?
          </label>
          <select
            value={formData.workStatus || ''}
            onChange={(e) => handleFieldChange('workStatus', e.target.value)}
            className="form-input"
          >
            <option value="">Select…</option>
            {WORK_STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Custom fields for injury-specific demographics */}
      {customFields.map(field => (
        <div key={field} className="field-group full-width">
          {field === 'dominantHand' && (
            <>
              <label className="field-label">
                Dominant Hand<span className="required">*</span>
              </label>
              <div className="radio-group">
                <label className="radio-option">
                  <input
                    type="radio"
                    name="dominantHand"
                    value="Right"
                    checked={formData.dominantHand === 'Right'}
                    onChange={(e) => handleFieldChange('dominantHand', e.target.value)}
                  />
                  <span>Right</span>
                </label>
                <label className="radio-option">
                  <input
                    type="radio"
                    name="dominantHand"
                    value="Left"
                    checked={formData.dominantHand === 'Left'}
                    onChange={(e) => handleFieldChange('dominantHand', e.target.value)}
                  />
                  <span>Left</span>
                </label>
              </div>
            </>
          )}

          {field === 'dominantLeg' && (
            <>
              <label className="field-label">
                Dominant Leg<span className="required">*</span>
              </label>
              <div className="radio-group">
                <label className="radio-option">
                  <input
                    type="radio"
                    name="dominantLeg"
                    value="Right"
                    checked={formData.dominantLeg === 'Right'}
                    onChange={(e) => handleFieldChange('dominantLeg', e.target.value)}
                  />
                  <span>Right</span>
                </label>
                <label className="radio-option">
                  <input
                    type="radio"
                    name="dominantLeg"
                    value="Left"
                    checked={formData.dominantLeg === 'Left'}
                    onChange={(e) => handleFieldChange('dominantLeg', e.target.value)}
                  />
                  <span>Left</span>
                </label>
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
};

/**
 * Consent Section
 * GDPR/Privacy consent and agreements
 * Updated: Added telehealth and audio recording consents
 */
export const ConsentSection = ({ formData, onChange }) => {
  const handleConsentChange = (consentId, value) => {
    onChange(consentId, value);
  };

  return (
    <div className="consent-section">
      <style jsx>{`
        .consent-section {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .intro-text {
          background: #dbeafe;
          padding: 16px;
          border-radius: 6px;
          border-left: 4px solid #2563eb;
          font-size: 14px;
          color: #1e40af;
          line-height: 1.6;
        }

        .consent-item {
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          padding: 16px;
          transition: border-color 0.2s;
        }

        .consent-item.checked {
          border-color: #2563eb;
          background: #eff6ff;
        }

        .consent-checkbox {
          display: flex;
          align-items: start;
          gap: 12px;
          cursor: pointer;
        }

        .consent-checkbox input {
          width: 20px;
          height: 20px;
          cursor: pointer;
          margin-top: 2px;
          flex-shrink: 0;
        }

        .consent-label {
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 6px;
        }

        .consent-description {
          font-size: 14px;
          color: #6b7280;
          line-height: 1.5;
        }

        .consent-note {
          font-size: 13px;
          color: #92400e;
          font-style: italic;
          margin-top: 8px;
          padding: 8px 12px;
          background: #fef3c7;
          border-radius: 4px;
        }

        .required {
          color: #dc2626;
          margin-left: 4px;
        }

        .signature-section {
          background: #f9fafb;
          border: 2px dashed #9ca3af;
          border-radius: 8px;
          padding: 20px;
          text-align: center;
        }

        .signature-input {
          width: 100%;
          max-width: 400px;
          padding: 10px;
          border: 2px solid #d1d5db;
          border-radius: 6px;
          text-align: center;
          font-size: 16px;
          margin-top: 10px;
        }

        .signature-date {
          margin-top: 10px;
          color: #6b7280;
          font-size: 14px;
        }

        .section-divider {
          border: none;
          border-top: 2px dashed #e5e7eb;
          margin: 8px 0;
        }

        .section-header {
          font-size: 16px;
          font-weight: 600;
          color: #374151;
          margin-bottom: 16px;
          padding-bottom: 8px;
          border-bottom: 2px solid #e5e7eb;
        }

        .appointment-consents {
          background: #f0fdf4;
          border: 2px solid #bbf7d0;
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 8px;
        }

        .appointment-consents .section-header {
          color: #166534;
          border-bottom-color: #bbf7d0;
        }

        .ai-disclosure {
          background: #f5f3ff;
          border: 2px solid #c4b5fd;
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 8px;
        }

        .ai-disclosure .section-header {
          color: #5b21b6;
          border-bottom-color: #c4b5fd;
        }

        .ai-info-box {
          background: #ede9fe;
          border-left: 4px solid #7c3aed;
          padding: 14px 16px;
          border-radius: 0 6px 6px 0;
          font-size: 14px;
          color: #4c1d95;
          line-height: 1.6;
          margin-bottom: 16px;
        }

        .ai-info-box ul {
          margin: 8px 0 0 0;
          padding-left: 20px;
        }

        .ai-info-box ul li {
          margin-bottom: 4px;
        }

        .ai-training-section {
          background: #fefce8;
          border: 2px solid #fde68a;
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 8px;
        }

        .ai-training-section .section-header {
          color: #92400e;
          border-bottom-color: #fde68a;
        }

        .ai-training-info {
          background: #fef9c3;
          border-left: 4px solid #eab308;
          padding: 14px 16px;
          border-radius: 0 6px 6px 0;
          font-size: 14px;
          color: #713f12;
          line-height: 1.6;
          margin-bottom: 16px;
        }

        .ai-training-info ul {
          margin: 8px 0 0 0;
          padding-left: 20px;
        }

        .ai-training-info ul li {
          margin-bottom: 4px;
        }
      `}</style>

      <div className="intro-text">
        Please read and agree to the following before proceeding with your patient history submission.
      </div>

      {/* NEW: Appointment Consents Section */}
      <div className="appointment-consents">
        <div className="section-header">
          Appointment Consents
        </div>

        {/* Telehealth Consent */}
        <div className={`consent-item ${formData.consentTelehealth ? 'checked' : ''}`} style={{ marginBottom: '16px' }}>
          <label className="consent-checkbox">
            <input
              type="checkbox"
              checked={formData.consentTelehealth || false}
              onChange={(e) => handleConsentChange('consentTelehealth', e.target.checked)}
            />
            <div>
              <div className="consent-label">
                Telehealth Consent
              </div>
              <div className="consent-description">
                After reviewing your referral and submitted history, Dr Armstrong will usually conduct an initial consultation via telehealth (phone) before arranging an in-person appointment as required.
                <br /><br />
                Telehealth may also be used for follow-up appointments when clinically appropriate.
                <br /><br />
                Do you consent to telehealth consultations as part of your care when appropriate?
              </div>
            </div>
          </label>
        </div>

        {/* Audio Recording Consent */}
        <div className={`consent-item ${formData.consentAudioRecording ? 'checked' : ''}`}>
          <label className="consent-checkbox">
            <input
              type="checkbox"
              checked={formData.consentAudioRecording || false}
              onChange={(e) => handleConsentChange('consentAudioRecording', e.target.checked)}
            />
            <div>
              <div className="consent-label">
                Audio Recording Consent
              </div>
              <div className="consent-description">
                I consent to my consultation being audio recorded for the purpose of preparing 
                consultation notes as a record of this appointment. These notes will be provided 
                to you within 1-2 working days.
              </div>
              <div className="consent-note">
                If you do not wish for this consultation to be recorded, please discuss with the doctor.
              </div>
            </div>
          </label>
        </div>
      </div>

      <hr className="section-divider" />

      <div className="section-header">
        Treatment & Privacy Consents
      </div>

      <div className={`consent-item ${formData.consentTreatment ? 'checked' : ''}`}>
        <label className="consent-checkbox">
          <input
            type="checkbox"
            checked={formData.consentTreatment || false}
            onChange={(e) => handleConsentChange('consentTreatment', e.target.checked)}
          />
          <div>
            <div className="consent-label">
              Treatment Consent<span className="required">*</span>
            </div>
            <div className="consent-description">
              I consent to assessment and treatment by the healthcare provider. I understand that
              I may withdraw this consent at any time.
            </div>
          </div>
        </label>
      </div>

      <div className={`consent-item ${formData.consentPrivacy ? 'checked' : ''}`}>
        <label className="consent-checkbox">
          <input
            type="checkbox"
            checked={formData.consentPrivacy || false}
            onChange={(e) => handleConsentChange('consentPrivacy', e.target.checked)}
          />
          <div>
            <div className="consent-label">
              Privacy & Information Collection<span className="required">*</span>
            </div>
            <div className="consent-description">
              I consent to the collection, storage, and use of my personal and health information
              for treatment purposes and as required by ACC. I understand my information will be
              kept confidential in accordance with the Privacy Act 2020.
            </div>
          </div>
        </label>
      </div>

      <div className={`consent-item ${formData.consentInformationSharing ? 'checked' : ''}`}>
        <label className="consent-checkbox">
          <input
            type="checkbox"
            checked={formData.consentInformationSharing || false}
            onChange={(e) => handleConsentChange('consentInformationSharing', e.target.checked)}
          />
          <div>
            <div className="consent-label">
              Information Sharing<span className="required">*</span>
            </div>
            <div className="consent-description">
              I consent to my healthcare provider sharing relevant medical information with ACC,
              my GP, and other involved healthcare professionals as necessary for my care and
              ACC claim processing.
            </div>
          </div>
        </label>
      </div>

      <div className={`consent-item ${formData.consentPayment ? 'checked' : ''}`}>
        <label className="consent-checkbox">
          <input
            type="checkbox"
            checked={formData.consentPayment || false}
            onChange={(e) => handleConsentChange('consentPayment', e.target.checked)}
          />
          <div>
            <div className="consent-label">
              Payment Agreement<span className="required">*</span>
            </div>
            <div className="consent-description">
              I understand that while ACC may cover some treatment costs, I may be responsible
              for co-payments, non-covered services, or costs if my claim is declined. I agree
              to pay any fees as they become due.
            </div>
          </div>
        </label>
      </div>

      <hr className="section-divider" />

      {/* AI Technology Disclosure Section */}
      <div className="ai-disclosure">
        <div className="section-header">
          Artificial Intelligence (AI) Technology
        </div>

        <div className="ai-info-box">
          ClinHx uses artificial intelligence (AI) technology as a core part of its service.
          AI is used to assist with processing your clinical information, generating consultation
          letters, and supporting clinical documentation. These AI functions are performed by
          Claude, developed by Anthropic (anthropic.com).
          <ul>
            <li>Your personal and health information is transmitted securely to the AI service using industry-standard encryption</li>
            <li>Your information is processed solely for the purpose of your clinical care and is not used to train AI models</li>
            <li>Information processed by the AI service is automatically deleted within 30 days in accordance with the data processor's retention policy</li>
            <li>All AI-generated clinical content is reviewed by your healthcare provider before being used in your care</li>
          </ul>
        </div>

        <div className="consent-item" style={{ padding: '15px', border: '2px solid #e5e7eb', borderRadius: '8px' }}>
          <div className="consent-label" style={{ marginBottom: '8px' }}>
            Use of AI Technology
          </div>
          <div className="consent-description" style={{ marginBottom: '12px' }}>
            I acknowledge and consent to AI technology being used in the processing of my health
            information as described above, in accordance with the Privacy Act 2020 and the
            Health Information Privacy Code 2020.
          </div>
          <div style={{ display: 'flex', gap: '20px', marginBottom: '8px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '16px' }}>
              <input
                type="radio"
                name="consentAI"
                checked={formData.consentAI === true}
                onChange={() => handleConsentChange('consentAI', true)}
                style={{ width: '20px', height: '20px', accentColor: '#16a34a' }}
              />
              Yes
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '16px' }}>
              <input
                type="radio"
                name="consentAI"
                checked={formData.consentAI === false}
                onChange={() => handleConsentChange('consentAI', false)}
                style={{ width: '20px', height: '20px', accentColor: '#dc2626' }}
              />
              No
            </label>
          </div>
          {formData.consentAI === false && (
            <div style={{ backgroundColor: '#fef9f0', borderLeft: '4px solid #e8941c', padding: '10px 12px', borderRadius: '0 5px 5px 0', fontSize: '14px', color: '#92400e' }}>
              Your form can still be submitted. The clinic will book your appointment manually.
            </div>
          )}
        </div>
      </div>

      {/* AI Training Data Opt-In Section */}
      <div className="ai-training-section">
        <div className="section-header">
          Contribution to Medical AI Research (Optional)
        </div>

        <div className="ai-training-info">
          We are committed to improving healthcare through responsible use of technology. You may
          choose to allow your fully anonymised health information to be used to help train and
          improve medical diagnostic and management AI models.
          <ul>
            <li>All personally identifiable information (name, date of birth, NHI number, contact details, and any other identifying information) will be permanently removed before your data is used</li>
            <li>Anonymised data may be used to improve the accuracy of AI tools in areas such as injury assessment, clinical decision support, and treatment planning</li>
            <li>This consent is entirely voluntary — your care will not be affected in any way whether you opt in or not</li>
            <li>You may withdraw this consent at any time by contacting us at the practice, and any identifiable link to your data will be removed</li>
          </ul>
        </div>

        <div className={`consent-item ${formData.consentAITraining ? 'checked' : ''}`}>
          <label className="consent-checkbox">
            <input
              type="checkbox"
              checked={formData.consentAITraining || false}
              onChange={(e) => handleConsentChange('consentAITraining', e.target.checked)}
            />
            <div>
              <div className="consent-label">
                AI Research Data Contribution
              </div>
              <div className="consent-description">
                I consent to my anonymised health information being used for the purpose of
                improving medical AI diagnostic and management models. I understand this is
                voluntary and I may withdraw this consent at any time.
              </div>
            </div>
          </label>
        </div>
      </div>

      <div className="signature-section">
        <div className="consent-label">
          Electronic Signature<span className="required">*</span>
        </div>
        <p className="consent-description" style={{ marginTop: '8px' }}>
          By typing your full name below, you are electronically signing this consent form
        </p>
        <input
          type="text"
          value={formData.electronicSignature || ''}
          onChange={(e) => handleConsentChange('electronicSignature', e.target.value)}
          placeholder="Type your full name"
          className="signature-input"
          required
        />
        <div className="signature-date">
          Date: {new Date().toLocaleDateString('en-NZ', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          })}
        </div>
      </div>
    </div>
  );
};

/**
 * Goals Section
 * Patient's treatment goals and expectations
 */
export const GoalsSection = ({ formData, onChange }) => {
  return (
    <div className="goals-section">
      <style jsx>{`
        .goals-section {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .intro-box {
          background: #fef3c7;
          border-left: 4px solid #f59e0b;
          padding: 14px;
          border-radius: 6px;
          font-size: 14px;
          color: #78350f;
        }

        .field-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .field-label {
          font-weight: 600;
          color: #1f2937;
          font-size: 15px;
        }

        .form-textarea {
          padding: 12px;
          border: 2px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
          font-family: inherit;
          line-height: 1.5;
          resize: vertical;
        }

        .form-textarea:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }

        .checkbox-group {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .checkbox-option {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px;
          border-radius: 6px;
          cursor: pointer;
          transition: background 0.2s;
        }

        .checkbox-option:hover {
          background: #f9fafb;
        }

        .checkbox-option input {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }
      `}</style>

      <div className="intro-box">
        <strong>Your Goals Matter!</strong> Understanding what you hope to achieve helps us
        create the most effective treatment plan for you.
      </div>

      <div className="field-group">
        <label className="field-label">
          What are your main goals for treatment?
        </label>
        <textarea
          value={formData.treatmentGoals || ''}
          onChange={(e) => onChange('treatmentGoals', e.target.value)}
          placeholder="e.g., Return to running, reduce pain to manage daily activities, improve sleep quality..."
          rows={4}
          className="form-textarea"
        />
      </div>

      <div className="field-group">
        <label className="field-label">
          What activities or tasks would you most like to return to?
        </label>
        <textarea
          value={formData.desiredActivities || ''}
          onChange={(e) => onChange('desiredActivities', e.target.value)}
          placeholder="e.g., Playing tennis, gardening, carrying grandchildren, going to gym..."
          rows={3}
          className="form-textarea"
        />
      </div>

      <div className="field-group">
        <label className="field-label">
          How soon do you need or want to achieve these goals?
        </label>
        <div className="checkbox-group">
          {[
            'As soon as possible (urgent)',
            'Within 1-2 weeks',
            'Within 1 month',
            'Within 3 months',
            'No specific timeframe'
          ].map(option => (
            <label key={option} className="checkbox-option">
              <input
                type="radio"
                name="goalTimeframe"
                value={option}
                checked={formData.goalTimeframe === option}
                onChange={(e) => onChange('goalTimeframe', e.target.value)}
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="field-group">
        <label className="field-label">
          Is there anything else you'd like us to know?
        </label>
        <textarea
          value={formData.additionalInfo || ''}
          onChange={(e) => onChange('additionalInfo', e.target.value)}
          placeholder="Any other concerns, questions, or information you think might be helpful..."
          rows={3}
          className="form-textarea"
        />
      </div>

      {/* GP Details Section */}
      <div style={{
        backgroundColor: '#f0f9ff',
        border: '1px solid #bae6fd',
        borderRadius: '8px',
        padding: '16px',
        marginTop: '8px'
      }}>
        <div style={{ fontWeight: '600', color: '#0369a1', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          🏥 Your GP Details
        </div>
        <p style={{ fontSize: '13px', color: '#0c4a6e', marginBottom: '12px' }}>
          We will send a copy of your consultation letter to your GP to keep them informed about your care.
        </p>
        
        <div style={{ display: 'grid', gap: '12px' }}>
          <div className="field-group">
            <label className="field-label" style={{ fontSize: '14px' }}>
              GP Name
            </label>
            <input
              type="text"
              value={formData.gpName || ''}
              onChange={(e) => onChange('gpName', e.target.value)}
              placeholder="e.g., Dr Sarah Jones"
              style={{
                padding: '10px 12px',
                border: '2px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                width: '100%',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div className="field-group">
            <label className="field-label" style={{ fontSize: '14px' }}>
              GP Practice/Clinic Name
            </label>
            <input
              type="text"
              value={formData.gpPractice || ''}
              onChange={(e) => onChange('gpPractice', e.target.value)}
              placeholder="e.g., Christchurch Medical Centre"
              style={{
                padding: '10px 12px',
                border: '2px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                width: '100%',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div className="field-group">
            <label className="field-label" style={{ fontSize: '14px' }}>
              GP Email (if known)
            </label>
            <input
              type="email"
              value={formData.gpEmail || ''}
              onChange={(e) => onChange('gpEmail', e.target.value)}
              placeholder="e.g., reception@medicalcentre.co.nz"
              style={{
                padding: '10px 12px',
                border: '2px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                width: '100%',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>
      </div>

      {/* Additional CC Recipients Section */}
      <div style={{
        backgroundColor: '#faf5ff',
        border: '1px solid #e9d5ff',
        borderRadius: '8px',
        padding: '16px',
        marginTop: '8px'
      }}>
        <div style={{ fontWeight: '600', color: '#7c3aed', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          📧 Additional Letter Recipients
        </div>
        <p style={{ fontSize: '13px', color: '#6b21a8', marginBottom: '12px' }}>
          Would you like a copy of your consultation letter sent to anyone else? (e.g., another specialist, employer, insurer)
        </p>
        
        <div style={{ display: 'grid', gap: '12px' }}>
          {/* Recipient 1 */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="field-group">
              <label className="field-label" style={{ fontSize: '14px' }}>
                Name
              </label>
              <input
                type="text"
                value={formData.additionalCc1Name || ''}
                onChange={(e) => onChange('additionalCc1Name', e.target.value)}
                placeholder="e.g., Dr John Smith"
                style={{
                  padding: '10px 12px',
                  border: '2px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  width: '100%',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div className="field-group">
              <label className="field-label" style={{ fontSize: '14px' }}>
                Email
              </label>
              <input
                type="email"
                value={formData.additionalCc1Email || ''}
                onChange={(e) => onChange('additionalCc1Email', e.target.value)}
                placeholder="email@example.com"
                style={{
                  padding: '10px 12px',
                  border: '2px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  width: '100%',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          {/* Recipient 2 */}
          {(formData.additionalCc1Name || formData.additionalCc1Email) && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div className="field-group">
                <label className="field-label" style={{ fontSize: '14px' }}>
                  Name (optional 2nd recipient)
                </label>
                <input
                  type="text"
                  value={formData.additionalCc2Name || ''}
                  onChange={(e) => onChange('additionalCc2Name', e.target.value)}
                  placeholder="e.g., Work Health Nurse"
                  style={{
                    padding: '10px 12px',
                    border: '2px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div className="field-group">
                <label className="field-label" style={{ fontSize: '14px' }}>
                  Email
                </label>
                <input
                  type="email"
                  value={formData.additionalCc2Email || ''}
                  onChange={(e) => onChange('additionalCc2Email', e.target.value)}
                  placeholder="email@example.com"
                  style={{
                    padding: '10px 12px',
                    border: '2px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Medical History Section
 * Collects medical conditions, medications, and allergies
 * Includes medication autocomplete with NZ common medications
 */

// NZ Sports Medicine Medications for autocomplete
const nzMedications = [
  // Pain Relief - Simple
  { generic: 'Paracetamol', brand: 'Panadol', use: 'Pain, fever' },
  { generic: 'Paracetamol/Codeine 8mg', brand: 'Panadeine 8/500', use: 'Mild-moderate pain' },
  { generic: 'Paracetamol/Codeine 16mg', brand: 'Panadeine 16/500', use: 'Moderate pain' },
  
  // NSAIDs - Anti-inflammatory
  { generic: 'Ibuprofen', brand: 'Nurofen', use: 'Pain, inflammation' },
  { generic: 'Naproxen', brand: 'Naprosyn', use: 'Musculoskeletal pain' },
  { generic: 'Diclofenac', brand: 'Voltaren', use: 'Pain, inflammation' },
  { generic: 'Celecoxib', brand: 'Celebrex', use: 'Anti-inflammatory (COX-2)' },
  { generic: 'Etoricoxib', brand: 'Arcoxia', use: 'Anti-inflammatory (COX-2)' },
  
  // Opioids
  { generic: 'Codeine', brand: 'Codeine phosphate', use: 'Moderate pain' },
  { generic: 'Tramadol', brand: 'Tramal', use: 'Moderate-severe pain' },
  { generic: 'Oxycodone', brand: 'OxyNorm', use: 'Severe pain' },
  { generic: 'Oxycodone SR', brand: 'OxyContin', use: 'Chronic severe pain' },
  { generic: 'Morphine SR', brand: 'M-Eslon', use: 'Chronic severe pain' },
  
  // Neuropathic Pain
  { generic: 'Amitriptyline', brand: 'Amitrip', use: 'Neuropathic pain, depression' },
  { generic: 'Nortriptyline', brand: 'Norpress', use: 'Neuropathic pain' },
  { generic: 'Gabapentin', brand: 'Neurontin', use: 'Neuropathic pain' },
  { generic: 'Pregabalin', brand: 'Lyrica', use: 'Neuropathic pain' },
  { generic: 'Capsaicin cream', brand: 'Zostrix', use: 'Topical neuropathic pain' },
  { generic: 'Clonidine patch', brand: 'Catapres TTS', use: 'Neuropathic pain' },
  
  // Muscle Relaxants
  { generic: 'Baclofen', brand: 'Lioresal', use: 'Muscle spasm' },
  { generic: 'Diazepam', brand: 'Valium', use: 'Muscle spasm, anxiety' },
  { generic: 'Orphenadrine', brand: 'Norflex', use: 'Muscle spasm' },
  
  // Corticosteroids
  { generic: 'Prednisone', brand: 'Prednisone', use: 'Inflammation, acute flare' },
  
  // Sleep / Sedation
  { generic: 'Zopiclone', brand: 'Imovane', use: 'Insomnia' },
  { generic: 'Quetiapine', brand: 'Seroquel', use: 'Sleep, anxiety' },
  
  // GI Protection / Laxatives
  { generic: 'Omeprazole', brand: 'Losec', use: 'Gastric protection with NSAIDs' },
  { generic: 'Hyoscine butylbromide', brand: 'Buscopan', use: 'Abdominal cramps' },
  { generic: 'Lactulose', brand: 'Duphalac', use: 'Constipation (opioid-induced)' },
  { generic: 'Docusate/Senna', brand: 'Coloxyl with Senna', use: 'Constipation' },
  
  // Antibiotics
  { generic: 'Amoxicillin', brand: 'Amoxil', use: 'Bacterial infection' },
  { generic: 'Amoxicillin/Clavulanate', brand: 'Augmentin', use: 'Bacterial infection' },
  { generic: 'Flucloxacillin', brand: 'Floxapen', use: 'Skin/soft tissue infection' },
  { generic: 'Doxycycline', brand: 'Doxycycline', use: 'Bacterial infection' },
  { generic: 'Trimethoprim', brand: 'Triprim', use: 'Urinary tract infection' },
  
  // Iron supplements
  { generic: 'Ferrous sulphate', brand: 'Ferro-Grad', use: 'Iron deficiency' },
  { generic: 'Ferrous fumarate', brand: 'Ferrograd', use: 'Iron deficiency' },
  
  // Respiratory / Allergy
  { generic: 'Salbutamol', brand: 'Ventolin', use: 'Asthma, bronchospasm' },
  { generic: 'Fluticasone/Salmeterol', brand: 'Seretide', use: 'Asthma' },
  { generic: 'Budesonide/Formoterol', brand: 'Symbicort', use: 'Asthma' },
  { generic: 'Fluticasone nasal', brand: 'Flixonase', use: 'Allergic rhinitis' },
  { generic: 'Loratadine', brand: 'Claratyne', use: 'Allergies, hay fever' },
  
  // Other common
  { generic: 'Aspirin', brand: 'Aspirin', use: 'Pain, blood thinner' },
];


// Get medication suggestions based on search term
const getMedicationSuggestions = (searchTerm) => {
  if (!searchTerm || searchTerm.length < 2) return [];
  
  const term = searchTerm.toLowerCase();
  const suggestions = [];
  
  nzMedications.forEach(med => {
    const genericMatch = med.generic.toLowerCase().includes(term);
    const brandMatch = med.brand.toLowerCase().includes(term);
    
    if (genericMatch || brandMatch) {
      // Prioritize matches that start with the term
      const startsWithGeneric = med.generic.toLowerCase().startsWith(term);
      const startsWithBrand = med.brand.toLowerCase().startsWith(term);
      
      suggestions.push({
        display: `${med.generic} (${med.brand})`,
        value: med.generic,
        brand: med.brand,
        use: med.use,
        priority: startsWithGeneric || startsWithBrand ? 0 : 1
      });
    }
  });
  
  // Sort by priority (startsWith first) then alphabetically
  suggestions.sort((a, b) => {
    if (a.priority !== b.priority) return a.priority - b.priority;
    return a.display.localeCompare(b.display);
  });
  
  return suggestions.slice(0, 10);
};

// Single medication input with autocomplete
const MedicationAutocomplete = ({ value, onChange, onRemove, placeholder, showRemove = true }) => {
  const [inputValue, setInputValue] = React.useState(value || '');
  const [suggestions, setSuggestions] = React.useState([]);
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [selectedIndex, setSelectedIndex] = React.useState(-1);

  React.useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    
    if (val.length >= 2) {
      const matches = getMedicationSuggestions(val);
      console.log('Medication search:', val, 'Found:', matches.length, 'matches');
      setSuggestions(matches);
      setShowSuggestions(matches.length > 0);
      setSelectedIndex(-1);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
    
    onChange(val);
  };

  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion.display);
    onChange(suggestion.display);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => prev < suggestions.length - 1 ? prev + 1 : prev);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          handleSuggestionClick(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  const handleBlur = () => {
    setTimeout(() => setShowSuggestions(false), 200);
  };

  return (
    <div style={{ position: 'relative', flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => inputValue.length >= 2 && suggestions.length > 0 && setShowSuggestions(true)}
          onBlur={handleBlur}
          placeholder={placeholder || "Start typing medication name..."}
          style={{
            flex: 1,
            padding: '10px 12px',
            border: '2px solid #d1d5db',
            borderRadius: '8px',
            fontSize: '14px',
            outline: 'none',
            transition: 'border-color 0.2s'
          }}
        />
        {showRemove && (
          <button
            type="button"
            onClick={onRemove}
            style={{
              padding: '8px 12px',
              backgroundColor: '#fee2e2',
              color: '#dc2626',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
            title="Remove medication"
          >
            ✕
          </button>
        )}
      </div>
      
      {showSuggestions && suggestions.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: showRemove ? '48px' : 0,
            backgroundColor: 'white',
            border: '2px solid #2563eb',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 9999,
            maxHeight: '250px',
            overflowY: 'auto',
            marginTop: '4px'
          }}
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              style={{
                padding: '10px 12px',
                cursor: 'pointer',
                backgroundColor: index === selectedIndex ? '#eff6ff' : 'white',
                borderBottom: index < suggestions.length - 1 ? '1px solid #e5e7eb' : 'none'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f3f4f6';
                setSelectedIndex(index);
              }}
              onMouseLeave={(e) => {
                if (index !== selectedIndex) {
                  e.target.style.backgroundColor = 'white';
                }
              }}
            >
              <div style={{ fontWeight: '500', color: '#111827' }}>
                {suggestion.display}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                {suggestion.use}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const MedicalHistorySection = ({ formData, onChange, additionalFields = [] }) => {
  // Initialize medications as array - handle string, array, or undefined
  const initMedications = () => {
    if (Array.isArray(formData.currentMedications)) {
      return formData.currentMedications.length > 0 ? formData.currentMedications : [''];
    }
    if (typeof formData.currentMedications === 'string' && formData.currentMedications.trim()) {
      return [formData.currentMedications];
    }
    return [''];
  };
  
  const medications = initMedications();
  
  const handleMedicationChange = (index, value) => {
    const newMeds = [...medications];
    newMeds[index] = value;
    onChange('currentMedications', newMeds);
  };

  const handleAddMedication = () => {
    if (medications.length < 15) {
      onChange('currentMedications', [...medications, '']);
    }
  };

  const handleRemoveMedication = (index) => {
    if (medications.length > 1) {
      const newMeds = medications.filter((_, i) => i !== index);
      onChange('currentMedications', newMeds);
    } else {
      onChange('currentMedications', ['']);
    }
  };

  return (
    <div className="medical-history-section">
      <style>{`
        .medical-history-section {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }
        .field-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .field-label {
          font-weight: 600;
          color: #1f2937;
          font-size: 14px;
        }
        .field-hint {
          font-size: 12px;
          color: #6b7280;
          margin-top: -4px;
        }
        .form-textarea {
          padding: 10px 12px;
          border: 2px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
          resize: vertical;
          font-family: inherit;
        }
        .form-textarea:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }
        .medications-container {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .add-medication-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 8px;
          padding: 10px 16px;
          background-color: #eff6ff;
          color: #2563eb;
          border: 2px dashed #93c5fd;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.2s;
        }
        .add-medication-btn:hover {
          background-color: #dbeafe;
          border-color: #60a5fa;
        }
        .section-card {
          background-color: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 16px;
        }
        .section-title {
          font-weight: 600;
          color: #374151;
          margin-bottom: 12px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
      `}</style>

      {/* Medical Conditions */}
      <div className="field-group">
        <label className="field-label">
          Do you have any medical conditions?
        </label>
        <p className="field-hint">
          Include: diabetes, heart disease, high blood pressure, asthma, arthritis, etc.
        </p>
        <textarea
          value={formData.medicalConditions || ''}
          onChange={(e) => onChange('medicalConditions', e.target.value)}
          placeholder="List any medical conditions you have been diagnosed with..."
          rows={3}
          className="form-textarea"
        />
      </div>

      {/* Current Medications with Autocomplete */}
      <div className="field-group">
        <label className="field-label">
          Current Medications
        </label>
        <p className="field-hint">
          Type at least 2 letters to see suggestions from common NZ medications
        </p>
        
        <div className="medications-container">
          {medications.map((med, index) => (
            <MedicationAutocomplete
              key={index}
              value={med}
              onChange={(value) => handleMedicationChange(index, value)}
              onRemove={() => handleRemoveMedication(index)}
              showRemove={medications.length > 1 || med !== ''}
              placeholder={`Medication ${index + 1} - start typing...`}
            />
          ))}
        </div>
        
        {medications.length < 15 && (
          <button
            type="button"
            onClick={handleAddMedication}
            className="add-medication-btn"
          >
            + Add Another Medication
          </button>
        )}
        
        {medications.length >= 15 && (
          <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
            Maximum of 15 medications reached
          </p>
        )}
      </div>

      {/* Allergies */}
      <div className="field-group">
        <label className="field-label">
          Allergies
        </label>
        <p className="field-hint">
          Include medication allergies, food allergies, and other allergies
        </p>
        <textarea
          value={formData.allergies || ''}
          onChange={(e) => onChange('allergies', e.target.value)}
          placeholder="e.g., Penicillin - causes rash, Codeine - nausea, Latex, Bee stings..."
          rows={2}
          className="form-textarea"
        />
      </div>

      {/* Previous Surgeries */}
      <div className="field-group">
        <label className="field-label">
          Previous Surgeries or Procedures
        </label>
        <textarea
          value={formData.previousSurgeries || ''}
          onChange={(e) => onChange('previousSurgeries', e.target.value)}
          placeholder="List any surgeries with approximate dates..."
          rows={2}
          className="form-textarea"
        />
      </div>

      {/* Social History */}
      <div className="section-card">
        <div className="section-title">
          Social History
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Occupation */}
          <div className="field-group">
            <label className="field-label">
              Occupation / Work
            </label>
            <textarea
              value={formData.occupation || ''}
              onChange={(e) => onChange('occupation', e.target.value)}
              placeholder="What is your job? Describe your typical work duties (e.g., desk work, manual labour, driving)..."
              rows={2}
              className="form-textarea"
            />
          </div>

          {/* Sport/Exercise */}
          <div className="field-group">
            <label className="field-label">
              Sport / Exercise / Hobbies
            </label>
            <textarea
              value={formData.sportExercise || ''}
              onChange={(e) => onChange('sportExercise', e.target.value)}
              placeholder="What sports or exercise do you do? How often? What level (recreational, competitive)?"
              rows={2}
              className="form-textarea"
            />
          </div>

          {/* Smoking — hidden for patients under 16 */}
          {(() => {
            const dob = formData.dateOfBirth;
            if (dob) {
              const today = new Date();
              const birth = new Date(dob);
              let age = today.getFullYear() - birth.getFullYear();
              const m = today.getMonth() - birth.getMonth();
              if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
              if (age < 16) return null;
            }
            return true;
          })() && (
          <div className="field-group">
            <label className="field-label">
              Smoking Status
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              {['Never smoked', 'Ex-smoker', 'Current smoker', 'Vape/e-cigarette'].map(option => (
                <label key={option} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="smokingStatus"
                    value={option}
                    checked={formData.smokingStatus === option}
                    onChange={(e) => onChange('smokingStatus', e.target.value)}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span style={{ fontSize: '14px' }}>{option}</span>
                </label>
              ))}
            </div>
            
            {/* Follow-up for current smokers */}
            {formData.smokingStatus === 'Current smoker' && (
              <div style={{ 
                marginTop: '16px', 
                padding: '16px', 
                backgroundColor: '#fef3c7', 
                borderRadius: '8px',
                border: '1px solid #fcd34d'
              }}>
                <div style={{ display: 'grid', gap: '12px' }}>
                  <div>
                    <label style={{ fontSize: '14px', fontWeight: '500', color: '#92400e', display: 'block', marginBottom: '6px' }}>
                      How much do you smoke per week?
                    </label>
                    <input
                      type="text"
                      value={formData.smokingAmount || ''}
                      onChange={(e) => onChange('smokingAmount', e.target.value)}
                      placeholder="e.g., 10 cigarettes per day, 1 pack per week"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '2px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '14px', fontWeight: '500', color: '#92400e', display: 'block', marginBottom: '6px' }}>
                      How long have you been smoking?
                    </label>
                    <input
                      type="text"
                      value={formData.smokingDuration || ''}
                      onChange={(e) => onChange('smokingDuration', e.target.value)}
                      placeholder="e.g., 5 years, 10+ years"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '2px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Follow-up for vapers */}
            {formData.smokingStatus === 'Vape/e-cigarette' && (
              <div style={{ 
                marginTop: '16px', 
                padding: '16px', 
                backgroundColor: '#fef3c7', 
                borderRadius: '8px',
                border: '1px solid #fcd34d'
              }}>
                <div style={{ display: 'grid', gap: '12px' }}>
                  <div>
                    <label style={{ fontSize: '14px', fontWeight: '500', color: '#92400e', display: 'block', marginBottom: '6px' }}>
                      How often do you vape?
                    </label>
                    <input
                      type="text"
                      value={formData.vapingFrequency || ''}
                      onChange={(e) => onChange('vapingFrequency', e.target.value)}
                      placeholder="e.g., Daily, occasionally, a few times a week"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '2px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '14px', fontWeight: '500', color: '#92400e', display: 'block', marginBottom: '6px' }}>
                      How long have you been vaping?
                    </label>
                    <input
                      type="text"
                      value={formData.vapingDuration || ''}
                      onChange={(e) => onChange('vapingDuration', e.target.value)}
                      placeholder="e.g., 1 year, 3 years"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '2px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Follow-up for ex-smokers */}
            {formData.smokingStatus === 'Ex-smoker' && (
              <div style={{ 
                marginTop: '16px', 
                padding: '16px', 
                backgroundColor: '#ecfdf5', 
                borderRadius: '8px',
                border: '1px solid #a7f3d0'
              }}>
                <div style={{ display: 'grid', gap: '12px' }}>
                  <div>
                    <label style={{ fontSize: '14px', fontWeight: '500', color: '#065f46', display: 'block', marginBottom: '6px' }}>
                      When did you quit?
                    </label>
                    <input
                      type="text"
                      value={formData.smokingQuitDate || ''}
                      onChange={(e) => onChange('smokingQuitDate', e.target.value)}
                      placeholder="e.g., 2 years ago, 2020"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '2px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '14px', fontWeight: '500', color: '#065f46', display: 'block', marginBottom: '6px' }}>
                      How long did you smoke for?
                    </label>
                    <input
                      type="text"
                      value={formData.smokingPreviousDuration || ''}
                      onChange={(e) => onChange('smokingPreviousDuration', e.target.value)}
                      placeholder="e.g., 10 years"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '2px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          )}

          {/* Alcohol — hidden for patients under 18 */}
          {(() => {
            const dob = formData.dateOfBirth;
            if (dob) {
              const today = new Date();
              const birth = new Date(dob);
              let age = today.getFullYear() - birth.getFullYear();
              const m = today.getMonth() - birth.getMonth();
              if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
              if (age < 18) return null;
            }
            return true;
          })() && (
          <div className="field-group">
            <label className="field-label">
              Alcohol Consumption
            </label>
            <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
              How many standard drinks do you have per week?
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' }}>
              {['None', 'Occasional (1-2/week)', 'Moderate (3-7/week)', 'Heavy (>7/week)'].map(option => (
                <label key={option} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="alcoholUse"
                    value={option}
                    checked={formData.alcoholUse === option}
                    onChange={(e) => onChange('alcoholUse', e.target.value)}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span style={{ fontSize: '14px' }}>{option}</span>
                </label>
              ))}
            </div>
            <div style={{ 
              backgroundColor: '#f0f9ff', 
              border: '1px solid #bae6fd', 
              borderRadius: '8px', 
              padding: '12px',
              fontSize: '12px',
              color: '#0369a1'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '6px' }}>What is 1 standard drink?</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '4px' }}>
                <span>• 330ml beer (4%)</span>
                <span>• 100ml wine (12.5%)</span>
                <span>• 30ml spirits (40%)</span>
              </div>
              <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #bae6fd', fontSize: '11px' }}>
                <strong>NZ Guidelines:</strong> No more than 10 standard drinks per week, and no more than 4 on any single day.
              </div>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Additional Fields from config */}
      {additionalFields && additionalFields.map((field, index) => (
        <div key={field.id || index} className="field-group">
          <label className="field-label">
            {field.label}
          </label>
          {field.note && <p className="field-hint">{field.note}</p>}
          {field.type === 'textarea' ? (
            <textarea
              value={formData[field.id] || ''}
              onChange={(e) => onChange(field.id, e.target.value)}
              placeholder={field.placeholder || ''}
              rows={field.rows || 2}
              className="form-textarea"
            />
          ) : (
            <input
              type={field.type || 'text'}
              value={formData[field.id] || ''}
              onChange={(e) => onChange(field.id, e.target.value)}
              placeholder={field.placeholder || ''}
              style={{
                padding: '10px 12px',
                border: '2px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          )}
        </div>
      ))}
    </div>
  );
};

/**
 * Medication Tried Input Component
 * Used for "What medications have you tried?" fields with autocomplete
 */
export const MedicationTriedInput = ({ value, onChange, label, placeholder }) => {
  // Parse existing value - can be string or array
  const initMedications = () => {
    if (Array.isArray(value)) {
      return value.length > 0 ? value : [''];
    }
    if (typeof value === 'string' && value.trim()) {
      // Split by common delimiters
      const meds = value.split(/[,;\n]/).map(m => m.trim()).filter(m => m);
      return meds.length > 0 ? meds : [''];
    }
    return [''];
  };

  const [medications, setMedications] = React.useState(initMedications());

  React.useEffect(() => {
    setMedications(initMedications());
  }, [value]);

  const handleMedicationChange = (index, newValue) => {
    const newMeds = [...medications];
    newMeds[index] = newValue;
    setMedications(newMeds);
    // Return as comma-separated string for storage
    onChange(newMeds.filter(m => m.trim()).join(', '));
  };

  const handleAddMedication = () => {
    if (medications.length < 10) {
      const newMeds = [...medications, ''];
      setMedications(newMeds);
    }
  };

  const handleRemoveMedication = (index) => {
    if (medications.length > 1) {
      const newMeds = medications.filter((_, i) => i !== index);
      setMedications(newMeds);
      onChange(newMeds.filter(m => m.trim()).join(', '));
    } else {
      setMedications(['']);
      onChange('');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 4px 0' }}>
        Type at least 2 letters to see medication suggestions
      </p>
      {medications.map((med, index) => (
        <MedicationAutocomplete
          key={index}
          value={med}
          onChange={(val) => handleMedicationChange(index, val)}
          onRemove={() => handleRemoveMedication(index)}
          showRemove={medications.length > 1 || med !== ''}
          placeholder={placeholder || `Medication ${index + 1} - did it help?`}
        />
      ))}
      {medications.length < 10 && (
        <button
          type="button"
          onClick={handleAddMedication}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginTop: '4px',
            padding: '8px 14px',
            backgroundColor: '#eff6ff',
            color: '#2563eb',
            border: '2px dashed #93c5fd',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          + Add Another Medication
        </button>
      )}
    </div>
  );
};

export default {
  DemographicsSection,
  ConsentSection,
  GoalsSection,
  MedicalHistorySection,
  MedicationTriedInput
};
